import { useState } from 'react';
import { Hash } from 'lucide-react';

interface NumberSliderProps {
  id: string;
  label: string;
  description?: string;
  value: string | number;
  min: number;
  max: number;
  step: number;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function NumberSlider({
  id,
  label,
  description,
  value,
  min,
  max,
  step,
  onChange,
  readOnly = false,
}: NumberSliderProps) {
  // Parse numeric value
  const parseValue = (val: string | number): number => {
    return typeof val === 'number' ? val : parseFloat(val);
  };

  const [numericValue, setNumericValue] = useState(parseValue(value));

  const handleSliderChange = (newValue: number) => {
    setNumericValue(newValue);
    onChange(String(newValue));
  };

  const handleInputChange = (newValue: string) => {
    const parsed = parseFloat(newValue);
    if (!isNaN(parsed) && parsed >= min && parsed <= max) {
      setNumericValue(parsed);
      onChange(String(parsed));
    }
  };

  return (
    <div className="token-input space-y-2">
      {/* Label */}
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* Slider + Value Input */}
      <div className="flex items-center space-x-3">
        <Hash className="w-4 h-4 text-gray-400 flex-shrink-0" />

        {/* Slider */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={numericValue}
          onChange={(e) => handleSliderChange(parseFloat(e.target.value))}
          disabled={readOnly}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary-600"
        />

        {/* Value Input */}
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={numericValue}
          onChange={(e) => handleInputChange(e.target.value)}
          disabled={readOnly}
          className="input w-20 text-sm text-right"
        />
      </div>

      {/* Range Labels */}
      <div className="flex justify-between text-xs text-gray-500">
        <span>{min}</span>
        <span>{max}</span>
      </div>
    </div>
  );
}
